import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * EBO: Rahmenhöhen (frame-height) decoration.
 *
 * The stock Horizon <variant-picker> is authoritative and fully visible for ALL options; this
 * block no longer renders its own variant grid and never drives the native radios. It is a passive
 * listener that mirrors the native picker's selection into:
 *
 *  1. The ERP-critical hidden cart line-item properties `properties[_PLU]` and
 *     `properties[lieferzeit]`, resynced from the newly selected variant on Horizon's
 *     `StandardEvents.productSelect` event, mirroring how product-form keeps the `id` input in sync.
 *  2. An "Art.-Nr.: <SKU> · <Versandinfo>" decoration with availability "Ampel" for the
 *     currently selected variant. The Art.-Nr. is the variant's sku (not the ERP-internal PLU);
 *     the Versandinfo text is built in Liquid from the exact same sources as
 *     blocks/ebo-versandinfo.liquid (variant metafield VariantInfo.Artikel_Lieferzeit_de,
 *     fallback ProductExtra.characteristics_2/_3), so the two can never disagree. A selected
 *     combination WITHOUT a storefront variant (ausverkaufte Rahmenhöhe whose variant the ERP
 *     feed removed — only its option value remains) renders as red Ampel + "Ausverkauft" and
 *     clears the ERP inputs, instead of silently keeping the previous variant's Art.-Nr.
 *  3. A recommended Körpergröße range written directly onto each of the native picker's
 *     Rahmenhöhe buttons (a second line under the size value, e.g. "57 cm" / "173 - 185 cm"),
 *     derived from the variant map. Only the size option's buttons are decorated — never
 *     Akkutyp/Rahmenform/Farbe. The stock picker morphs its fieldsets on every variant change and
 *     combined-listing child switch, wiping the injected spans; the ProductSelectEvent promise
 *     resolves AFTER variant-picker.js has morphed the DOM (see fetchUpdatedSection), so we
 *     re-apply there, with a rAF-debounced MutationObserver on the section as a safety net for any
 *     morph that bypasses the event. The apply routine is strictly idempotent (a second pass makes
 *     zero DOM writes), so observer-triggered re-application always terminates.
 *
 * Resilience: the stock <variant-picker> morphs (re-renders) on every variant change and on
 * combined-listing child switches, so the current variant id is re-read from the DOM on each event.
 * This block is a sibling of the picker and is never morphed, so its hidden inputs and decoration
 * panel persist across changes — we only update their values.
 *
 * Combined-Listings-safe: switching between grouped child products is an in-page morph of <main>,
 * NOT a full navigation, so the <script data-ebo-variant-map> JSON is morph-updated in place. We
 * therefore NEVER cache the parsed map across product changes — it is re-read FRESH at every use so
 * the ERP-critical _PLU / lieferzeit always reflect the CURRENT product's selected variant.
 *
 * @extends {Component}
 */
class EboVariantButtons extends Component {
  /** @type {Element | null} */
  #section = null;

  /** @type {MutationObserver | null} */
  #morphObserver = null;

  /** @type {number | null} */
  #decorateRafId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section, dialog');

    // Listen to Horizon's real variant-change event on the section, exactly like
    // product-price.js / product-form.js do.
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    // Seed the ERP inputs + decoration from whatever is currently selected in the DOM.
    this.#syncFromCurrentSelection();

    // Initial paint of the Körpergröße ranges on the native picker's size buttons. Runs as soon as
    // this module executes (deferred module — right after HTML parse), before first interaction.
    this.#decorateSizeButtons();

    // Safety net: re-apply the button decoration after ANY morph of the section (variant change,
    // combined-listing navigation, section re-render). We observe the section rather than the
    // <variant-picker> itself because a `main` morph may replace the picker element. Observing the
    // section is not observing ourselves-only — our own writes also trigger it — but the decorate
    // routine is idempotent and we flush self-caused records via takeRecords(), so this settles.
    if (this.#section) {
      this.#morphObserver = new MutationObserver(this.#scheduleDecorate);
      this.#morphObserver.observe(this.#section, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#morphObserver?.disconnect();
    this.#morphObserver = null;
    if (this.#decorateRafId != null) cancelAnimationFrame(this.#decorateRafId);
  }

  /**
   * Reads and parses the <script data-ebo-variant-map> JSON FRESH from the DOM on every call.
   * MUST NOT be cached: on a combined-listing morph this element's instance is preserved while the
   * script's text is morph-updated to the new child product's variants — a cached map would be stale
   * and would send WRONG _PLU / lieferzeit values to the cart/ERP.
   * @returns {Record<string, {plu: string, sku: string, size: string | null, koerper_von: string | null, koerper_bis: string | null, koerper_einheit: string | null, lieferzeit: string, versand: string, ampel: string, selectable: boolean}>}
   */
  #getVariantMap() {
    const script = this.querySelector('script[type="application/json"][data-ebo-variant-map]');
    if (!script?.textContent) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[ebo-variant-buttons] Could not parse variant map:', error);
      return {};
    }
  }

  /** @returns {HTMLElement | null} The stock variant picker in the same section. */
  #getVariantPicker() {
    return this.#section?.querySelector('variant-picker') ?? document.querySelector('variant-picker');
  }

  /** @returns {string | null} The variant id currently checked in the stock picker. */
  #getCurrentVariantId() {
    const picker = this.#getVariantPicker();
    const checkedRadios = /** @type {HTMLInputElement[]} */ (
      [...(picker?.querySelectorAll('input[type="radio"]:checked') ?? [])]
    );

    // When the picker positively shows a selection, it alone decides. A checked radio without
    // `data-variant-id` means the selected combination has NO storefront variant (e.g. an
    // ausverkaufte Rahmenhöhe whose variant the ERP feed removed while its option value remains)
    // — return null then; the buy-buttons `id` input still holds the PREVIOUS/first-available
    // variant in that state and must not win as a fallback.
    if (checkedRadios.length > 0) {
      return checkedRadios.find((radio) => radio.dataset.variantId)?.dataset.variantId ?? null;
    }

    // Fallback (dropdown-style pickers without radios): the buy-buttons form `id` input.
    const idInput = /** @type {HTMLInputElement | null} */ (
      this.#section?.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')
    );
    return idInput?.value || null;
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #onProductSelect = (event) => {
    // Ignore selections coming from a product card elsewhere on the page.
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const variantId =
          detail?.resource?.id != null ? String(detail.resource.id) : this.#getCurrentVariantId();
        if (variantId) this.#applyVariant(variantId);
        else if (this.#selectionHasNoVariant()) this.#applyNoVariant();
        this.#syncVersandinfo(detail?.html);
        // The promise resolves after variant-picker.js has morphed the picker (which wipes the
        // injected Körpergröße spans) — re-apply against the fresh DOM and fresh variant map.
        this.#decorateSizeButtons();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[ebo-variant-buttons] productSelect promise rejected:', error);
        }
        // Best-effort resync from the DOM even if the promise rejected.
        const variantId = this.#getCurrentVariantId();
        if (variantId) this.#applyVariant(variantId);
        else if (this.#selectionHasNoVariant()) this.#applyNoVariant();
        this.#decorateSizeButtons();
      });
  };

  /** Seeds the ERP inputs + decoration from whatever is currently selected in the DOM. */
  #syncFromCurrentSelection() {
    const variantId = this.#getCurrentVariantId();
    if (variantId) this.#applyVariant(variantId);
    else if (this.#selectionHasNoVariant()) this.#applyNoVariant();
  }

  /**
   * Whether the stock picker POSITIVELY shows a selected combination that has no storefront
   * variant: checked radios exist, but none carries `data-variant-id` (the server omits it when
   * `product_option_value.variant` is nil, e.g. for ausverkaufte Rahmenhöhen whose variants the
   * ERP feed removed). Deliberately stricter than a null #getCurrentVariantId — a missing picker
   * or empty selection must NOT be rendered as "Ausverkauft".
   * @returns {boolean}
   */
  #selectionHasNoVariant() {
    const picker = this.#getVariantPicker();
    if (!picker) return false;
    const checkedRadios = /** @type {HTMLInputElement[]} */ (
      [...picker.querySelectorAll('input[type="radio"]:checked')]
    );
    return checkedRadios.length > 0 && checkedRadios.every((radio) => !radio.dataset.variantId);
  }

  /**
   * Renders the panel for a selection WITHOUT a storefront variant (ausverkaufte Rahmenhöhe):
   * red Ampel + "Ausverkauft" instead of a stale Art.-Nr. of the previously selected variant,
   * and cleared ERP inputs so no stale _PLU / lieferzeit could ever be submitted.
   */
  #applyNoVariant() {
    const pluInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-ebo-plu-input]'));
    const lieferzeitInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-ebo-lieferzeit-input]')
    );

    if (pluInput) pluInput.value = '';
    if (lieferzeitInput) lieferzeitInput.value = '';

    const info = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-ebo-selected-info], [ref="selectedInfo"]')
    );
    if (!info) return;

    info.innerHTML = '<span class="ebo-variant-buttons__ampel ebo-variant-buttons__ampel--red"></span>Ausverkauft';
  }

  /**
   * Mirrors the selected variant into the ERP hidden inputs and the decoration panel.
   * @param {string} variantId
   */
  #applyVariant(variantId) {
    // Read the map FRESH so it reflects the current (possibly morphed) product's variants.
    const data = this.#getVariantMap()[variantId];
    if (!data) return;

    const pluInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-ebo-plu-input]'));
    const lieferzeitInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-ebo-lieferzeit-input]')
    );

    if (pluInput) pluInput.value = data.plu ?? '';
    if (lieferzeitInput) lieferzeitInput.value = data.lieferzeit ?? '';

    this.#renderSelectedInfo(data);
  }

  /**
   * Renders the selected-variant line: Ampel + "Art.-Nr.: <SKU> · <Versandinfo>". Die
   * Körpergröße wird hier nicht mehr gerendert (sie steht bereits auf den Rahmenhöhen-Buttons,
   * siehe #decorateSizeButtons). Ampel weiterhin aus VariantInfo.Verfuegbarkeit.
   * @param {{sku: string, versand: string, ampel: string}} data
   */
  #renderSelectedInfo(data) {
    const info = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-ebo-selected-info], [ref="selectedInfo"]')
    );
    if (!info) return;

    const ampel = data.ampel || 'red';
    const artikelParts = [];
    if (data.sku) artikelParts.push(`Art.-Nr.: ${this.#escape(String(data.sku))}`);
    // versand kann <br>-Markup aus dem vertrauenswürdigen PIM-Metafeld enthalten (siehe
    // blocks/ebo-versandinfo.liquid) — deshalb bewusst nicht escaped.
    if (data.versand) artikelParts.push(String(data.versand));

    if (artikelParts.length === 0) {
      info.innerHTML = '';
      return;
    }

    info.innerHTML = `<span class="ebo-variant-buttons__ampel ebo-variant-buttons__ampel--${ampel}"></span>${artikelParts.join(
      ' · '
    )}`;
  }

  /**
   * Keeps the separate Versand line (blocks/ebo-versandinfo.liquid) in sync on variant changes.
   * On a plain variant change Horizon morphs only the picker itself, and that line is pure
   * Liquid — so we adopt its freshly rendered counterpart from the product HTML the
   * ProductSelectEvent promise delivers. That reuses the block's full Liquid logic (variant
   * metafield with product-level characteristics fallback) instead of duplicating it here.
   * No-op when the block is absent (editor configuration) or the fetched document lacks it
   * (e.g. quick-add's product-card section rendering).
   * @param {Document | undefined} html
   */
  #syncVersandinfo(html) {
    const current = /** @type {HTMLElement | null} */ (this.#section?.querySelector('[data-ebo-versandinfo]'));
    const next = html?.querySelector('[data-ebo-versandinfo]');
    if (!current || !next) return;
    current.innerHTML = next.innerHTML;
    current.toggleAttribute('hidden', next.hasAttribute('hidden'));
  }

  /** rAF-debounced trampoline for the MutationObserver — coalesces morph bursts into one apply. */
  #scheduleDecorate = () => {
    if (this.#decorateRafId != null) return;
    this.#decorateRafId = requestAnimationFrame(() => {
      this.#decorateRafId = null;
      this.#decorateSizeButtons();
    });
  };

  /**
   * Lowercases and folds German umlauts so JS matching mirrors Liquid's `handleize`-based size
   * detection ('Rahmenhöhe' → 'rahmenhohe', 'Größe' → 'grosse').
   * @param {string | null | undefined} name
   * @returns {string}
   */
  #normalizeOptionName(name) {
    return (name || '')
      .toLowerCase()
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss');
  }

  /**
   * @param {string | null | undefined} name
   * @returns {boolean} Whether the option name denotes the size / Rahmenhöhe option.
   */
  #isSizeOptionName(name) {
    const normalized = this.#normalizeOptionName(name);
    return (
      normalized.includes('grosse') ||
      normalized.includes('groesse') ||
      normalized.includes('rahmenhohe') ||
      normalized.includes('rahmenhoehe') ||
      normalized.includes('size')
    );
  }

  /**
   * Builds size value → Körpergröße range from the variant map. Multiple variants (colors,
   * battery types) share a size; they should agree, but if they differ we take the min-von /
   * max-bis union.
   * @returns {Map<string, {von: string, bis: string, einheit: string}>}
   */
  #getSizeRanges() {
    /** @type {Map<string, {von: string, bis: string, einheit: string, vonNum: number, bisNum: number}>} */
    const ranges = new Map();

    for (const data of Object.values(this.#getVariantMap())) {
      const size = data?.size != null ? String(data.size) : '';
      if (!size) continue;

      const von = data.koerper_von != null && data.koerper_von !== '' ? String(data.koerper_von) : '';
      const bis = data.koerper_bis != null && data.koerper_bis !== '' ? String(data.koerper_bis) : '';
      const einheit = data.koerper_einheit != null ? String(data.koerper_einheit) : '';
      if (!von && !bis) continue;

      const vonNum = Number.parseFloat(von);
      const bisNum = Number.parseFloat(bis);

      const existing = ranges.get(size);
      if (!existing) {
        ranges.set(size, { von, bis, einheit, vonNum, bisNum });
        continue;
      }

      if (von && (!existing.von || (Number.isFinite(vonNum) && Number.isFinite(existing.vonNum) && vonNum < existing.vonNum))) {
        existing.von = von;
        existing.vonNum = vonNum;
      }
      if (bis && (!existing.bis || (Number.isFinite(bisNum) && Number.isFinite(existing.bisNum) && bisNum > existing.bisNum))) {
        existing.bis = bis;
        existing.bisNum = bisNum;
      }
      if (!existing.einheit && einheit) existing.einheit = einheit;
    }

    return ranges;
  }

  /**
   * @param {{von: string, bis: string, einheit: string} | undefined} range
   * @returns {string} Display text for the button's second line, or '' when nothing to show.
   */
  #formatKoerperRange(range) {
    if (!range) return '';
    const unit = range.einheit ? ` ${range.einheit}` : '';
    if (range.von && range.bis) return `${range.von} - ${range.bis}${unit}`;
    if (range.von) return `ab ${range.von}${unit}`;
    if (range.bis) return `bis ${range.bis}${unit}`;
    return '';
  }

  /**
   * @returns {HTMLInputElement[]} The native picker's size-option radios (button style only).
   * Primary: the EBO group markers (`data-option-name` on the `.variant-option` fieldset, present
   * on both store branches' pickers). Fallback: the stock per-radio `data-option-name` attribute.
   */
  #getSizeOptionRadios() {
    const picker = this.#getVariantPicker();
    if (!picker) return [];

    for (const fieldset of picker.querySelectorAll('fieldset.variant-option--buttons[data-option-name]')) {
      if (this.#isSizeOptionName(fieldset.getAttribute('data-option-name'))) {
        return /** @type {HTMLInputElement[]} */ ([...fieldset.querySelectorAll('input[type="radio"]')]);
      }
    }

    return /** @type {HTMLInputElement[]} */ (
      [...picker.querySelectorAll('fieldset.variant-option--buttons input[type="radio"][data-option-name]')]
    ).filter((radio) => this.#isSizeOptionName(radio.dataset.optionName));
  }

  /**
   * Writes the recommended Körpergröße range onto each of the native picker's Rahmenhöhe buttons
   * as a second line. Idempotent: an up-to-date button is left untouched (zero DOM writes on a
   * repeat pass), a stale hint is updated, and a hint whose range disappeared (or that a morph
   * duplicated) is removed. Degrades to plain buttons when the picker, the size option, or the
   * metafield data cannot be found — never throws, never alters non-size options.
   */
  #decorateSizeButtons() {
    try {
      const radios = this.#getSizeOptionRadios();
      if (radios.length === 0) return;

      const ranges = this.#getSizeRanges();

      for (const radio of radios) {
        const label = radio.closest('.variant-option__button-label');
        // Swatch-style labels have no text line to extend — leave them alone.
        if (!label || label.classList.contains('variant-option__button-label--has-swatch')) continue;

        const text = this.#formatKoerperRange(ranges.get(radio.value));
        const hints = label.querySelectorAll('.ebo-variant-buttons__button-koerper');

        // Drop duplicates a morph may have produced, keeping the first.
        for (let i = 1; i < hints.length; i += 1) hints[i].remove();
        const hint = hints[0];

        if (!text) {
          hint?.remove();
          continue;
        }

        if (hint) {
          if (hint.textContent !== text) hint.textContent = text;
        } else {
          const span = document.createElement('span');
          span.className = 'ebo-variant-buttons__button-koerper';
          span.setAttribute('aria-hidden', 'true');
          span.textContent = text;
          label.appendChild(span);
        }
      }

      // Discard the mutation records our own writes just produced so the observer safety net does
      // not schedule a (harmless but pointless) follow-up pass.
      this.#morphObserver?.takeRecords();
    } catch (error) {
      console.warn('[ebo-variant-buttons] Could not decorate size buttons:', error);
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escape(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

if (!customElements.get('ebo-variant-buttons')) {
  customElements.define('ebo-variant-buttons', EboVariantButtons);
}
